import type {
  ThunkAction,
  ThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit';
import type { User } from '@/shared/api/types';
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';

import type { AuthAdapter } from '@/platform/auth/AuthAdapter';

import type { RootState } from './rootReducer';

export type { RootState } from './rootReducer';

/**
 * Third argument of every thunk, supplied by `createAppStore({ extra })` from
 * the app definition, so auth thunks stay app-agnostic.
 */
export interface AppThunkExtra {
  auth: AuthAdapter;
  /** Runs after any successful login (password, MFA, Google, token). */
  onAuthenticated?: (ctx: {
    dispatch: AppDispatch;
    user: Partial<User>;
  }) => void;
}

export type AppDispatch = ThunkDispatch<RootState, AppThunkExtra, UnknownAction>;
export type AppThunk<R = void> = ThunkAction<
  R,
  RootState,
  AppThunkExtra,
  UnknownAction
>;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
